// FeedbackRoadmapPage.tsx — /feedback/roadmap: status-grouped roadmap view.
//
// Reached from the board's "Roadmap preview" rail panel (View full roadmap)
// and a header link on FeedbackPage — there is no separate global nav entry
// for it (judgment call: it's a view WITHIN the feedback feature, matching
// how e.g. a post's Communities panel links into /communities rather than
// getting its own rail item).
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Button } from '@/ui';
import { Icon } from '@/icons';
import { ROADMAP_BUCKET_LABELS, useFeedbackRoadmapBuckets } from '@/hooks/useFeedback';
import type { RoadmapBucketKey } from '@/hooks/useFeedback';
import { useRightRail, RailGroup, RailPanel } from '@/app/shell/RightRail';
import { FEEDBACK_CATEGORY_LABELS } from '@/core/models/feedback';
import { CATEGORY_ICONS } from './feedbackMeta';
import { FeedbackHowItWorksPanel } from './panels/FeedbackHowItWorksPanel';

const BUCKET_ORDER: RoadmapBucketKey[] = ['planned', 'in-progress', 'testing', 'completed'];

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
  text-decoration: none;

  &:hover {
    color: ${({ theme }) => theme.color.textPrimary};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

const Title = styled.h1`
  margin: 0 0 4px;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: 700;
  color: ${({ theme }) => theme.color.textPrimary};
`;

const Subtitle = styled.p`
  margin: 0 0 18px;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
`;

const Columns = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const Column = styled.section`
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
`;

const ColumnHead = styled.h2`
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 700;
  color: ${({ theme }) => theme.color.textPrimary};
  padding-bottom: 9px;
  border-bottom: 1px solid ${({ theme }) => theme.color.glassBorder};
`;

const ColumnCount = styled.span`
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.color.textMuted};
`;

const Card = styled(Link)`
  display: block;
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => (theme.cardsGlass ? 'transparent' : theme.color.surface)};
  text-decoration: none;
  color: inherit;


  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

const CardTitle = styled.span`
  display: block;
  margin-bottom: 6px;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 600;
  color: ${({ theme }) => theme.color.textPrimary};
`;

const CardMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const CategoryChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const Votes = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-weight: 700;
  color: ${({ theme }) => theme.color.brandFrom};
`;

const Empty = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const ErrorBlock = styled.div`
  padding: 24px;
  text-align: center;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => (theme.cardsGlass ? 'transparent' : theme.color.surface)};
`;

const ErrorText = styled.p`
  margin: 0 0 12px;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
`;

export function FeedbackRoadmapPage() {
  const { buckets, loading, error, reload } = useFeedbackRoadmapBuckets();

  const rail = useMemo(
    () => (
      <RailGroup pageKey="feedback-roadmap">
        <RailPanel id="how-it-works" label="How it works">
          <FeedbackHowItWorksPanel />
        </RailPanel>
      </RailGroup>
    ),
    [],
  );
  useRightRail(rail);

  return (
    <section>
      <BackLink to="/feedback">
        <Icon name="arrow-left" size={14} /> Back to feedback
      </BackLink>
      <Title>Roadmap</Title>
      <Subtitle>Where every planned, in-progress and shipped item stands right now.</Subtitle>

      {error ? (
        <ErrorBlock role="alert">
          <ErrorText>Could not load the roadmap. Please try again.</ErrorText>
          <Button variant="ghost" onClick={reload}>
            Try again
          </Button>
        </ErrorBlock>
      ) : (
        <Columns>
          {BUCKET_ORDER.map((bucket) => {
            const matches = buckets[bucket];
            return (
              <Column key={bucket} aria-label={ROADMAP_BUCKET_LABELS[bucket]}>
                <ColumnHead>
                  {ROADMAP_BUCKET_LABELS[bucket]}
                  <ColumnCount>{matches.length}</ColumnCount>
                </ColumnHead>
                {loading ? (
                  <Empty>Loading…</Empty>
                ) : matches.length === 0 ? (
                  <Empty>Nothing here yet.</Empty>
                ) : (
                  matches.map((item) => (
                    <Card key={item.id} to={`/feedback/${item.id}`}>
                      <CardTitle>{item.title}</CardTitle>
                      <CardMeta>
                        <CategoryChip>
                          <Icon name={CATEGORY_ICONS[item.category]} size={11} />
                          {FEEDBACK_CATEGORY_LABELS[item.category]}
                        </CategoryChip>
                        <Votes>
                          <Icon name="trending-up" size={11} />
                          {item.upvotes}
                        </Votes>
                      </CardMeta>
                    </Card>
                  ))
                )}
              </Column>
            );
          })}
        </Columns>
      )}
    </section>
  );
}
